import React, { useContext, useEffect, useState } from "react";
import {
  Button, FormControl, InputLabel, MenuItem, Select, Tab, Tabs, TextField, Typography
} from "@material-ui/core";
import { Alert, Autocomplete, Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../../../services/api";
import { AuthContext } from "../../../../context/Auth/AuthContext";
import PortfolioTab from "./PortfolioTab";
import AlertsTab from "./AlertsTab";
import ProjectTab from "./ProjectTab";
import EfficiencyTab from "./EfficiencyTab";
import ResultsTab from "./ResultsTab";
import QualityTab from "./QualityTab";

const TABS = [
  { id: "portfolio", label: "Portfólio" },
  { id: "alerts", label: "Alertas" },
  { id: "project", label: "Projeto" },
  { id: "efficiency", label: "Eficiência" },
  { id: "results", label: "Resultado" },
  { id: "quality", label: "Qualidade dos dados" }
];
const TAB_STORAGE_KEY = "dashboard:implantation:tab";

const localDate = date => date.toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
const defaultEnd = () => localDate(new Date());
const defaultStart = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return localDate(date);
};
const validDate = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};
const readTab = () => {
  try {
    const stored = window.localStorage.getItem(TAB_STORAGE_KEY);
    return TABS.some(tab => tab.id === stored) ? stored : "portfolio";
  } catch (error) {
    return "portfolio";
  }
};
const message = error => error?.response?.data?.error || error?.response?.data?.message
  || "Não foi possível carregar o dashboard de implantação. Tente novamente.";

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(1, 0, 2), minWidth: 0 },
  title: { fontWeight: 700, lineHeight: 1.2 },
  subtitle: { color: theme.palette.text.secondary, fontSize: 13, marginTop: 4 },
  filters: {
    display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: theme.spacing(1.5),
    margin: theme.spacing(2, 0, 1)
  },
  field: {
    minWidth: 160,
    [theme.breakpoints.down("xs")]: { width: "100%", minWidth: 0 }
  },
  wideField: {
    minWidth: 240,
    [theme.breakpoints.down("xs")]: { width: "100%", minWidth: 0 }
  },
  tabs: { borderBottom: `1px solid ${theme.palette.divider}`, marginBottom: theme.spacing(2) },
  tab: { textTransform: "none", minWidth: 0, padding: theme.spacing(0.75, 2), fontWeight: 600 },
  meta: { fontSize: 11, color: theme.palette.text.secondary, marginBottom: theme.spacing(1) }
}));

const ImplantationView = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";
  const [filters, setFilters] = useState(() => ({ start: defaultStart(), end: defaultEnd(), type: "", client: null, technicianId: "" }));
  const [validationDays, setValidationDays] = useState(30);
  const [tab, setTab] = useState(readTab);
  const [projectId, setProjectId] = useState(null);
  const [reload, setReload] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const datesValid = validDate(filters.start) && validDate(filters.end) && filters.start <= filters.end;

  useEffect(() => {
    if (!datesValid) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    api.get("/dashboard/implantation", {
      params: {
        start_date: filters.start,
        end_date: filters.end,
        ...(filters.type ? { type: filters.type } : {}),
        ...(filters.client ? { client_id: filters.client.id } : {}),
        ...(filters.technicianId ? { technician_id: filters.technicianId } : {}),
        validation_days: validationDays,
        ...(reload ? { refresh: 1 } : {})
      }
    })
      .then(response => { if (active) setData(response.data); })
      .catch(err => { if (active) setError(message(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filters, validationDays, reload, datesValid]);

  const changeFilter = (key, value) => setFilters(previous => ({ ...previous, [key]: value }));
  const changeTab = value => {
    setTab(value);
    try {
      window.localStorage.setItem(TAB_STORAGE_KEY, value);
    } catch (storageError) {
      // Preferência opcional; o dashboard funciona sem armazenamento local.
    }
  };
  const openProject = id => {
    setProjectId(id);
    changeTab("project");
  };

  const options = data?.options || { tipos: [], clientes: [], tecnicos: [] };

  const renderTab = () => {
    if (!data) return null;
    switch (tab) {
      case "alerts":
        return <AlertsTab data={data} isAdmin={isAdmin} onLimitsSaved={() => setReload(value => value + 1)} />;
      case "project":
        return <ProjectTab projects={data.projects} projectId={projectId} onSelectProject={setProjectId} reloadToken={reload} />;
      case "efficiency":
        return <EfficiencyTab data={data} />;
      case "results":
        return <ResultsTab data={data} />;
      case "quality":
        return <QualityTab data={data} validationDays={validationDays} onValidationDaysChange={setValidationDays} />;
      default:
        return <PortfolioTab data={data} onOpenProject={openProject} />;
    }
  };

  return (
    <div className={classes.root}>
      <Typography variant="h6" component="h2" className={classes.title}>Implantação</Typography>
      <div className={classes.subtitle}>
        Projetos de implantação, reimplantação e recursos: OS pai no grupo Gestão de Projetos com etapas no grupo Implantação Etapas.
      </div>

      <div className={classes.filters} role="search" aria-label="Filtros do dashboard de implantação">
        <TextField id="implantation-start-date" type="date" label="De" variant="outlined" size="small" className={classes.field}
          value={filters.start} InputLabelProps={{ shrink: true }}
          onChange={event => changeFilter("start", event.target.value)}
          error={!datesValid} helperText={!datesValid ? "Período inválido." : undefined} />
        <TextField id="implantation-end-date" type="date" label="Até" variant="outlined" size="small" className={classes.field}
          value={filters.end} InputLabelProps={{ shrink: true }}
          onChange={event => changeFilter("end", event.target.value)} error={!datesValid} />
        <FormControl variant="outlined" size="small" className={classes.field}>
          <InputLabel id="implantation-type-label" shrink>Tipo de projeto</InputLabel>
          <Select labelId="implantation-type-label" label="Tipo de projeto" value={filters.type} displayEmpty notched
            onChange={event => changeFilter("type", event.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {options.tipos.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
          </Select>
        </FormControl>
        <Autocomplete
          className={classes.wideField}
          size="small"
          options={options.clientes}
          value={filters.client}
          onChange={(_, value) => changeFilter("client", value)}
          getOptionLabel={option => option.nome}
          getOptionSelected={(option, value) => option.id === value.id}
          noOptionsText="Nenhum cliente com projeto"
          renderInput={params => (
            <TextField {...params} label="Cliente" variant="outlined" placeholder="Todos" InputLabelProps={{ shrink: true }} />
          )}
        />
        <FormControl variant="outlined" size="small" className={classes.field}>
          <InputLabel id="implantation-technician-label" shrink>Técnico</InputLabel>
          <Select labelId="implantation-technician-label" label="Técnico" value={filters.technicianId} displayEmpty notched
            onChange={event => changeFilter("technicianId", event.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {options.tecnicos.map(technician => (
              <MenuItem key={technician.id} value={String(technician.id)}>{technician.nome}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} style={{ height: 40 }}
          onClick={() => setReload(value => value + 1)} disabled={loading || !datesValid}>
          Reconsultar
        </Button>
      </div>

      {data && (
        <div className={classes.meta}>
          {data.projects.length} projeto(s) no período · atualizado em {new Date(data.generated_at).toLocaleString("pt-BR")}
          {loading ? " · atualizando…" : ""}
        </div>
      )}

      <Tabs value={tab} onChange={(_, value) => changeTab(value)} indicatorColor="primary" textColor="primary"
        variant="scrollable" scrollButtons="auto" className={classes.tabs} aria-label="Painéis de implantação">
        {TABS.map(item => <Tab key={item.id} value={item.id} label={item.label} className={classes.tab} />)}
      </Tabs>

      {error ? <Alert severity="error">{error}</Alert>
        : !data && loading ? (
          <div role="status" aria-label="Carregando dashboard de implantação">
            <Skeleton variant="rect" height={96} />
            <br />
            <Skeleton variant="rect" height={320} />
          </div>
        ) : renderTab()}
    </div>
  );
};

export default ImplantationView;
